import React, { useContext, useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { AppCtx, ViewTypes } from '../../../AppCtx';
import SongsPageComponent from "./SongsPageComponent";
import PresentationSongsPageComponent from "../presentation/components/PresentationSongsPageComponent";
import useScreenWakeLock from '../../../shared/hooks/useScreenWakeLock';
import { CLIENT_PROCESS_ID } from '../../../infrastructure/events/channels';
import { useSongContentService } from '../hooks/useSongContentService';
import { useSelectSong } from '../../navigation/components/Navigation';
import {useWorkspaceReadOnly} from '../../../shared/hooks/useWorkspaceReadOnly';

const MonacoEditorComponent = lazy(() => import('../monaco/MonacoEditorComponent'));

const ShortcutsHelp = ({ onClose }) => (
  <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: 'var(--background-color)', color: 'var(--text-color)', padding: '20px', borderRadius: '8px', border: '1px solid var(--border-color-verse)', boxShadow: '0 0 10px var(--shortcuts-shadow)', zIndex: 10000 }} onClick={onClose}>
    <h3 style={{ marginTop: 0 }}>Skróty klawiszowe</h3>
    <table>
      <tbody style={{ color: 'var(--text-color)' }}>
        {[
          [['F5', 'F11', 'F'], 'Tryb prezentacji'],
          [['C'], 'Pokazuj/ukryj akordy'],
          [['M'], 'Pokazuj/ukryj menu'],
          [['L'], 'Zwiń/rozwiń wszystkie pieśni'],
          [['B', '.'], 'Czarny ekran'],
          [['H', 'F1'], 'Pokaż/ukryj pomoc'],
          [['Esc'], 'Wyjście z trybu prezentacji']
        ].map((row, i) => (
          <tr key={i}>
            <td>{row[0].map(key => <kbd key={key}>{key}</kbd>).reduce((prev, curr) => [prev, ' lub ', curr])}</td>
            <td>{row[1]}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <div style={{ textAlign: 'center', marginTop: '10px', color: 'var(--meta-background-color-alt)' }}>
      Kliknij gdziekolwiek aby zamknąć
    </div>
  </div>
);

const SongsViewComponent = ({ children }) => {
  const isReadOnly = useWorkspaceReadOnly();
  const [showShortcuts, setShowShortcuts] = useState(false);
  const {
    viewType,
    selectedFile,
    isPresentationMode,
    isFullScreen,
    setIsPresentationMode,
    setIsTopMenuVisible,
    setIsFileBrowserVisible,
    setAreChordsVisible,
    setIsFullScreen,
    setCollapsedSongs,
    isBlankScreen,
    setIsBlankScreen,
    sharedPresentationState,
    isCommonSessionEnabled,
    setPresentationControlPanelVisible
  } = useContext(AppCtx);
  useScreenWakeLock(Boolean(selectedFile?.found && (isFullScreen || isPresentationMode)));
  const selectSong = useSelectSong();
  const wasPresentationModeRef = useRef(isPresentationMode);
  const lastAppliedRemoteSongRef = useRef({ fileName: null, songNumber: null });
  const lastProcessedPresentationStateRef = useRef(null);
  
  // Get songsJson from the hook instead of from context
  const { songsJson } = useSongContentService();

  useEffect(() => {
    if (process.env.REACT_APP_HOSANNA_DIRECT === '1' || !isCommonSessionEnabled || !sharedPresentationState) {
      if (!sharedPresentationState) {
        lastAppliedRemoteSongRef.current = { fileName: null, songNumber: null };
        lastProcessedPresentationStateRef.current = null;
      }
      return;
    }

    const { fileName, songNumber, __processId, updatedAt } = sharedPresentationState;

    if (updatedAt && lastProcessedPresentationStateRef.current === updatedAt) {
      return;
    }

    if (!fileName || songNumber == null || __processId === CLIENT_PROCESS_ID) {
      if (updatedAt) {
        lastProcessedPresentationStateRef.current = updatedAt;
      }
      return;
    }

    const normalizedSongNumber = typeof songNumber === 'number'
      ? songNumber
      : parseInt(songNumber, 10);

    if (!Number.isFinite(normalizedSongNumber)) {
      if (updatedAt) {
        lastProcessedPresentationStateRef.current = updatedAt;
      }
      return;
    }

    const selectedNormalizedSongNumber = typeof selectedFile?.songNumber === 'number'
      ? selectedFile.songNumber
      : parseInt(selectedFile?.songNumber ?? '', 10);

    if (
      selectedFile?.name === fileName &&
      Number.isFinite(selectedNormalizedSongNumber) &&
      selectedNormalizedSongNumber === normalizedSongNumber
    ) {
      if (updatedAt) {
        lastProcessedPresentationStateRef.current = updatedAt;
      }
      return;
    }

    if (
      lastAppliedRemoteSongRef.current.fileName === fileName &&
      lastAppliedRemoteSongRef.current.songNumber === normalizedSongNumber
    ) {
      if (updatedAt) {
        lastProcessedPresentationStateRef.current = updatedAt;
      }
      return;
    }

    lastAppliedRemoteSongRef.current = { fileName, songNumber: normalizedSongNumber };
    if (updatedAt) {
      lastProcessedPresentationStateRef.current = updatedAt;
    }
    selectSong(fileName, normalizedSongNumber, false, false, { produceEvent: false, pushState: false });
  }, [isCommonSessionEnabled, sharedPresentationState, selectSong, selectedFile]);
  
  useEffect(() => {
    const handleKeyDown = e => {
      if (process.env.REACT_APP_HOSANNA_DIRECT === '1' && isPresentationMode && e.key !== 'Escape') return;
      if ((document.activeElement && ['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName) ||
        document.activeElement?.isContentEditable) ||
        (selectedFile.found && viewType === ViewTypes.MONACO)) {
        return;
      }

      if (e.key === 'h' || e.key === 'F1') {
        e.preventDefault();
        setShowShortcuts(prev => !prev);
      } else if (e.key === 'Escape') {
        showShortcuts && setShowShortcuts(false);
        if (isPresentationMode) {
          setIsPresentationMode(false);
          setIsTopMenuVisible(true);
          setIsFileBrowserVisible(true);
          setIsFullScreen(false);
          setIsBlankScreen(false);
        }
      } else if (selectedFile.found) {
        if (e.key === 'F5' || e.key === 'F11' || (e.key.toLowerCase() === 'f' && (e.ctrlKey || e.metaKey || e.altKey))) {
          e.preventDefault();
          const newState = !isPresentationMode;
          setIsPresentationMode(newState);
          setIsTopMenuVisible(!newState);
          setIsFileBrowserVisible(!newState);
          setIsFullScreen(newState);
        } else if (e.key === 'c') {
          setAreChordsVisible(prev => !prev);
        } else if (e.key.toLowerCase() === 'm') {
          setIsTopMenuVisible(prev => !prev);
          setIsFileBrowserVisible(prev => !prev);
        } else if (e.key.toLowerCase() === 'l') {
          if (!selectedFile?.name) return;
          setCollapsedSongs(prev => {
            const newMap = new Map(prev);
            const fileCollapsed = newMap.get(selectedFile.name) || new Set();
            
            // If no songs are collapsed or not all are collapsed, collapse all
            // Otherwise, expand all
            if (fileCollapsed.size === 0 || fileCollapsed.size < (songsJson?.songs?.length || 0)) {
              newMap.set(selectedFile.name, new Set(Array.from({ length: songsJson?.songs?.length || 0 }, (_, i) => i)));
            } else {
              newMap.set(selectedFile.name, new Set());
            }
            
            return newMap;
          });
        } else if (e.key.toLowerCase() === 'b' || e.key === '.') {
          setIsBlankScreen(prev => !prev);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [setIsPresentationMode, setAreChordsVisible, isPresentationMode, setIsTopMenuVisible,
    setIsFullScreen, setIsFileBrowserVisible, setCollapsedSongs, songsJson, setIsBlankScreen,
    viewType, selectedFile, showShortcuts]);

  useEffect(() => {
    if (wasPresentationModeRef.current && !isPresentationMode) {
      setPresentationControlPanelVisible(false);
    }
    wasPresentationModeRef.current = isPresentationMode;
  }, [isPresentationMode, setPresentationControlPanelVisible]);

  return (
    <div data-zoom-scope={isPresentationMode ? "presentation" : (isReadOnly || viewType === ViewTypes.SONGS) ? "song" : "app"}>
      {selectedFile.found && (
        (isReadOnly || viewType === ViewTypes.SONGS)
          ? isPresentationMode && process.env.REACT_APP_HOSANNA_DIRECT !== '1' ? <PresentationSongsPageComponent /> : <SongsPageComponent children={children} />
          : <Suspense fallback={<p role="status">Ładowanie edytora…</p>}><MonacoEditorComponent children={children} /></Suspense>
      )}

      {showShortcuts && <ShortcutsHelp onClose={() => setShowShortcuts(false)} />}
      
      {process.env.REACT_APP_HOSANNA_DIRECT !== '1' && isBlankScreen && isPresentationMode && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'black', zIndex: 9999, cursor: 'none' }}
          onClick={() => setIsBlankScreen(false)} />
      )}
    </div>
  );
};

export default SongsViewComponent;
